import type { components } from '@octokit/openapi-webhooks-types';
import type { Endpoints } from '@octokit/types';
import type { PullRequest as GitHubWebhookPullRequest } from '@octokit/webhooks-types';
import type { FragmentType } from '../graphql';
import { getFragmentData } from '../graphql';
import { PullSummaryFragmentDoc } from '../graphql/generated/graphql';
import type { MergeableStateEnum } from '../drizzle/schema';
import type {
  PullCommitMetadata,
  PullCompareCommits,
  PullEditableFields,
  PullExternalIds,
  PullIdentifier,
  PullMetadata,
  PullRecord,
  PullState,
} from './index';

/**
 * GitHub Pull Request Adapter
 *
 * Transforms data between GitHub's API format and Mesa's domain models.
 * All GitHub-specific transformations should be centralized here.
 */
export namespace GitHubPullAdapter {
  // Type aliases for GitHub API responses
  export type GitHubPullRequest = Endpoints['GET /repos/{owner}/{repo}/pulls/{pull_number}']['response']['data'];
  export type GitHubCommit =
    Endpoints['GET /repos/{owner}/{repo}/pulls/{pull_number}/commits']['response']['data'][number];
  export type GitHubCompareCommits = Endpoints['GET /repos/{owner}/{repo}/compare/{base}...{head}']['response']['data'];
  export type SynchronizePull = components['schemas']['webhook-pull-request-synchronize']['pull_request'];
  export type WebhookPull = components['schemas']['pull-request-webhook'];

  /**
   * Converts GitHub's state/draft/merged fields to Mesa's single state field
   */
  export const toPullState = ({
    state,
    merged,
    draft,
  }: {
    state: 'open' | 'closed';
    merged: boolean;
    draft?: boolean;
  }): PullState => {
    if (draft && state === 'open') return 'DRAFT';
    if (merged) return 'MERGED';
    if (state === 'open') return 'OPEN';
    return 'CLOSED';
  };

  /**
   * Converts Mesa's state back to GitHub's format
   */
  export const fromPullState = (
    state: PullState
  ): {
    state: 'open' | 'closed';
    merged: boolean;
    draft: boolean;
  } => {
    switch (state) {
      case 'DRAFT':
        return { state: 'open', merged: false, draft: true };
      case 'MERGED':
        return { state: 'closed', merged: true, draft: false };
      case 'OPEN':
        return { state: 'open', merged: false, draft: false };
      case 'CLOSED':
        return { state: 'closed', merged: false, draft: false };
    }
  };

  /**
   * Transforms GitHub pull request to minimal Mesa metadata
   */
  export const toPullMetadata = (
    githubPull: GitHubPullRequest | GitHubWebhookPullRequest | WebhookPull | SynchronizePull
  ): PullMetadata => {
    return {
      id: githubPull.id,
      nodeId: githubPull.node_id,
      owner: githubPull.base.repo.owner!.login,
      repo: githubPull.base.repo.name,
      number: githubPull.number,
      title: githubPull.title,
      body: githubPull.body ?? '',
      state: toPullState({
        state: githubPull.state,
        merged: githubPull.merged ?? false,
        draft: githubPull.draft,
      }),
      labels: githubPull.labels.map((l) => ({ name: l.name, color: l.color })),
      base: {
        ref: githubPull.base.ref,
        sha: githubPull.base.sha,
      },
      head: {
        ref: githubPull.head.ref,
        sha: githubPull.head.sha,
      },
    };
  };

  /**
   * Transforms GitHub REST API pull request to complete Mesa pull record
   */
  export const toPullRecord = (
    githubPull: GitHubPullRequest
  ): Omit<PullRecord, 'reviewDecision' | 'checkStatusRollup'> => {
    return {
      nodeId: githubPull.node_id,
      id: githubPull.id,
      owner: githubPull.base.repo.owner.login,
      repo: githubPull.base.repo.name,
      number: githubPull.number,
      state: toPullState({
        state: githubPull.state,
        merged: githubPull.merged ?? false,
        draft: githubPull.draft,
      }),
      title: githubPull.title,
      body: githubPull.body ?? '',
      commentCount: githubPull.comments,
      mergeable: githubPull.mergeable ?? false,
      rebaseable: githubPull.rebaseable ?? false,
      additions: githubPull.additions,
      deletions: githubPull.deletions,
      author: {
        id: githubPull.user.id,
        login: githubPull.user.login,
        name: githubPull.user.name,
        avatarUrl: githubPull.user.avatar_url,
      },
      authorAssociation: githubPull.author_association,
      labels: githubPull.labels.map((l) => ({ name: l.name, color: l.color })),
      head: {
        ref: githubPull.head.ref,
        sha: githubPull.head.sha,
      },
      base: {
        ref: githubPull.base.ref,
        sha: githubPull.base.sha,
      },
      mergeableState: githubPull.mergeable_state.toUpperCase() as (typeof MergeableStateEnum.enumValues)[number],
      mergedAt: githubPull.merged_at,
      mergeCommitSha: githubPull.merge_commit_sha,
      mergedBy: githubPull.merged_by
        ? {
            id: githubPull.merged_by.id,
            login: githubPull.merged_by.login,
            name: githubPull.merged_by.name,
            avatarUrl: githubPull.merged_by.avatar_url,
          }
        : null,
      closedAt: githubPull.closed_at,
      requestedReviewers: githubPull.requested_reviewers
        ? githubPull.requested_reviewers.map((r) => ({
            id: r.id,
            login: 'login' in r ? r.login : undefined,
            name: r.name,
            avatarUrl: 'avatar_url' in r ? r.avatar_url : undefined,
          }))
        : [],
      assignees: githubPull.assignees
        ? githubPull.assignees.map((a) => ({
            id: a.id,
            login: a.login,
            avatarUrl: a.avatar_url,
          }))
        : [],
      updatedAt: githubPull.updated_at,
      createdAt: githubPull.created_at,
    };
  };

  /**
   * Transforms GitHub webhook pull request to Mesa pull record
   */
  export const toPullRecordFromWebhook = (
    githubPull: GitHubWebhookPullRequest
  ): Omit<PullRecord, 'owner' | 'repo' | 'reviewDecision' | 'checkStatusRollup'> => {
    return {
      nodeId: githubPull.node_id,
      id: githubPull.id,
      author: {
        id: githubPull.user.id,
        login: githubPull.user.login,
        name: githubPull.user.name,
        avatarUrl: githubPull.user.avatar_url,
      },
      number: githubPull.number,
      state: toPullState({
        state: githubPull.state,
        merged: githubPull.merged ?? false,
        draft: githubPull.draft,
      }),
      title: githubPull.title,
      body: githubPull.body ?? '',
      commentCount: githubPull.comments,
      mergeable: githubPull.mergeable ?? false,
      rebaseable: githubPull.rebaseable ?? false,
      additions: githubPull.additions,
      deletions: githubPull.deletions,
      authorAssociation: githubPull.author_association,
      labels: githubPull.labels.map((l) => ({ name: l.name, color: l.color })),
      head: {
        ref: githubPull.head.ref,
        sha: githubPull.head.sha,
      },
      base: {
        ref: githubPull.base.ref,
        sha: githubPull.base.sha,
      },
      mergeableState: githubPull.mergeable_state.toUpperCase() as (typeof MergeableStateEnum.enumValues)[number],
      mergedAt: githubPull.merged_at,
      mergeCommitSha: githubPull.merge_commit_sha,
      mergedBy: githubPull.merged_by
        ? {
            id: githubPull.merged_by.id,
            login: githubPull.merged_by.login,
            name: githubPull.merged_by.login, // Note: webhook uses login as name
            avatarUrl: githubPull.merged_by.avatar_url,
          }
        : null,
      closedAt: githubPull.closed_at,
      requestedReviewers: githubPull.requested_reviewers
        ? githubPull.requested_reviewers.map((r) => ({
            id: r.id,
            login: 'login' in r ? r.login : undefined,
            name: r.name,
            avatarUrl: 'avatar_url' in r ? r.avatar_url : undefined,
          }))
        : [],
      assignees: githubPull.assignees
        ? githubPull.assignees.map((a) => ({
            id: a.id,
            login: a.login,
            avatarUrl: a.avatar_url,
          }))
        : [],
      updatedAt: githubPull.updated_at,
      createdAt: githubPull.created_at,
    };
  };

  /**
   * Transforms GitHub GraphQL search fragment to Mesa pull record
   */
  export const toPullRecordFromSearchFragment = (props: {
    pull: FragmentType<typeof PullSummaryFragmentDoc>;
  }): PullRecord => {
    const pull = getFragmentData(PullSummaryFragmentDoc, props.pull);
    const authorType = pull.author!.__typename;

    // Validation: Check for corrupted SHA in GraphQL response
    if (pull.headRefOid && pull.headRefOid.startsWith('/')) {
      throw new Error(`Corrupted SHA detected in GraphQL response: ${pull.headRefOid}`);
    }

    return {
      nodeId: pull.id,
      id: Number(pull.fullDatabaseId),
      author: {
        id: Number(pull.author!.id),
        login: pull.author!.login,
        name:
          authorType === 'User' || authorType === 'Organization' || authorType === 'EnterpriseUserAccount'
            ? pull.author!.name
            : undefined,
        avatarUrl: pull.author!.avatarUrl,
      },
      authorAssociation: pull.authorAssociation,
      owner: pull.repository.owner.login,
      repo: pull.repository.name,
      number: pull.number,
      state: (pull.isDraft ? 'DRAFT' : pull.state) as PullState,
      title: pull.title,
      body: pull.body,
      mergeable: pull.mergeable === 'MERGEABLE',
      mergedAt: pull.mergedAt ?? null,
      mergeableState: pull.mergeStateStatus,
      rebaseable: pull.canBeRebased,
      mergedBy: pull.mergedBy
        ? {
            id: Number(pull.mergedBy.id),
            login: pull.mergedBy.login,
            name:
              pull.mergedBy.__typename === 'User' ||
              pull.mergedBy.__typename === 'Organization' ||
              pull.mergedBy.__typename === 'EnterpriseUserAccount'
                ? pull.mergedBy.name
                : undefined,
            avatarUrl: pull.mergedBy.avatarUrl,
          }
        : null,
      mergeCommitSha: pull.mergeCommit?.oid ?? null,
      reviewDecision: pull.reviewDecision ?? null,
      checkStatusRollup: pull.statusCheckRollup?.state ?? null,
      head: {
        ref: pull.headRefName,
        sha: pull.headRefOid,
      },
      base: {
        ref: pull.baseRefName,
        sha: pull.baseRefOid,
      },
      labels:
        pull.labels?.nodes?.map((label) => ({
          name: label!.name,
          color: label!.color,
        })) ?? [],
      commentCount:
        pull.comments.totalCount +
        (pull.reviewThreads.nodes?.reduce((acc, t) => acc + (t?.comments.totalCount ?? 0), 0) ?? 0),
      requestedReviewers:
        pull.reviewRequests?.nodes?.map((reviewer) => {
          if (reviewer!.requestedReviewer?.__typename === 'Team') {
            return {
              id: reviewer!.requestedReviewer.databaseId!,
              login: reviewer!.requestedReviewer.teamName,
              name: reviewer!.requestedReviewer.teamName,
              avatarUrl: reviewer!.requestedReviewer.teamAvatarUrl as string,
            };
          }
          return {
            id: reviewer!.requestedReviewer!.databaseId!,
            login: reviewer!.requestedReviewer!.login,
            avatarUrl: reviewer!.requestedReviewer!.avatarUrl as string,
            name: undefined as string | undefined,
          };
        }) ?? [],
      assignees:
        pull.assignees?.nodes?.map((assignee) => ({
          id: assignee!.databaseId!,
          login: assignee!.login,
          avatarUrl: assignee!.avatarUrl as string,
        })) ?? [],
      additions: pull.additions,
      deletions: pull.deletions,
      updatedAt: pull.updatedAt,
      createdAt: pull.createdAt,
      closedAt: pull.closedAt ?? null,
    };
  };

  /**
   * Transforms GitHub commit to Mesa commit metadata
   */
  export const toPullCommitMetadata = (commit: GitHubCommit): PullCommitMetadata => {
    return {
      sha: commit.sha,
      message: commit.commit.message,
      date: commit.commit.author?.date ?? commit.commit.committer?.date ?? '',
      files: commit.files?.map((f) => f.filename),
    };
  };

  /**
   * Transforms GitHub compare commits response to Mesa format
   */
  export const toPullCompareCommits = (compareCommits: GitHubCompareCommits): PullCompareCommits => {
    return {
      commits: compareCommits.commits.map(toPullCommitMetadata),
      files: compareCommits.files?.map((f) => f.filename),
    };
  };

  /**
   * Prepares Mesa pull data for GitHub API update
   */
  export const toGitHubUpdateParams = (pull: PullEditableFields) => {
    const params: any = {};

    if (pull.title) params.title = pull.title;
    if (pull.body !== undefined) params.body = pull.body;

    if (pull.state) {
      const { state, draft } = fromPullState(pull.state);
      params.state = state;
      if (draft !== undefined) params.draft = draft;
    }

    return params;
  };

  /**
   * Extract pull identifier from various GitHub formats
   */
  export const extractPullIdentifier = (
    githubPull: GitHubPullRequest | GitHubWebhookPullRequest | WebhookPull | SynchronizePull
  ): PullIdentifier => {
    return {
      owner: githubPull.base.repo.owner!.login,
      repo: githubPull.base.repo.name,
      number: githubPull.number,
    };
  };

  /**
   * Extract external IDs from GitHub pull request
   */
  export const extractExternalIds = (
    githubPull: GitHubPullRequest | GitHubWebhookPullRequest | WebhookPull | SynchronizePull
  ): PullExternalIds => {
    return {
      id: githubPull.id,
      nodeId: githubPull.node_id,
    };
  };
}
